import { getEnsuredLayerItem } from 'src/lib/tabPaneHelper';
import type { FetchGeoItemsParams, Location, MapElemInfo } from 'src/models';
import type { GeoItemsMeta, GIResource, GIResourceWithID, MapSelectOpts } from 'src/models/geoItem';
import type { MapElemState } from 'src/composables/useMapSelectionCore';

interface UseMapSelectionGeoItemNavDeps {
  geoItemsMeta: GeoItemsMeta;
  mapElem: MapElemState;
  setMapSelectedElemInfo: (val: MapElemInfo<GIResource | Location> | null) => void;
  afterDataLayerItemClick: (dataName: string, obj: GIResource, opts?: MapSelectOpts) => void;
  changeGeoItemLayers: (dataName: string) => Promise<void>;
}

export interface MapSelectionGeoItemNavContext {
  onTabElementClick: (element: MapElemInfo<GIResourceWithID>) => Promise<void>;
  showPreviousGeoItem: (current: MapElemInfo<GIResourceWithID>) => Promise<void>;
  showNextGeoItem: (current: MapElemInfo<GIResourceWithID>) => Promise<void>;
  getGeoItemById: (obj: FetchGeoItemsParams) => Promise<void>;
}

/**
 * タブ内geoItemのナビゲーション(前後切替・タブクリック)。
 * 選択反映は core.afterDataLayerItemClick / core.setMapSelectedElemInfo に一方向依存する。
 */
export function useMapSelectionGeoItemNav(deps: UseMapSelectionGeoItemNavDeps): MapSelectionGeoItemNavContext {
  const { geoItemsMeta, mapElem, setMapSelectedElemInfo, afterDataLayerItemClick } = deps;

  const setCommentDisplayedOnLayer = async() => {
    // 付箋を開いてなければ開く
    const dataName = 'comment';
    if (!geoItemsMeta.show[dataName]) {
      geoItemsMeta.show[dataName] = true;
      await deps.changeGeoItemLayers(dataName);
    }
  };
  const onTabElementClick = async(element: MapElemInfo<GIResourceWithID>) => {
    if (element.dataName === 'comment') {
      await setCommentDisplayedOnLayer();
    }
    // 選択状態にする
    const layerManager = geoItemsMeta.map[element.dataName].layerManager;
    layerManager.deselectAll();
    const selectedLayerItem = getEnsuredLayerItem(layerManager, element.data);
    const hasGeoLocation = !!selectedLayerItem.location.lat && !!selectedLayerItem.location.lon;
    if (hasGeoLocation) {
      layerManager.updateLayerItem(selectedLayerItem);
    }
    afterDataLayerItemClick(element.dataName, selectedLayerItem, { moveCenter: hasGeoLocation });
  };
  const switchGeoItem = (current: MapElemInfo<GIResourceWithID>, move: number) => {
    const geoItemLayerManager = geoItemsMeta.map[current.dataName].layerManager;
    const nextGeoItem = geoItemLayerManager.getGeoItemByIndexDiff(current.data, move);
    geoItemLayerManager.deselectAll();
    nextGeoItem.isSelected = true;
    const hasGeoLocation = !!nextGeoItem.location.lat && !!nextGeoItem.location.lon;
    if (hasGeoLocation) {
      geoItemLayerManager.updateLayerItem(nextGeoItem);
    }
    afterDataLayerItemClick(current.dataName, nextGeoItem, { moveCenter: hasGeoLocation });
  };
  const showPreviousGeoItem = async(current: MapElemInfo<GIResourceWithID>) => {
    switchGeoItem(current, -1);
  };
  const showNextGeoItem = async(current: MapElemInfo<GIResourceWithID>) => {
    switchGeoItem(current, 1);
  };
  const getGeoItemById = async(obj: FetchGeoItemsParams) => {
    const metaItem = geoItemsMeta.map[obj.dataName];
    if (!obj.id) return;

    try {
      const resource = await metaItem.manager.getResourceById(obj.id);
      if (!resource) return;

      metaItem.layerManager.addLayerItem(resource);
    } catch {
      mapElem.getGeoItemByIdFailed++;
    }
    // 同じGeoItemを再表示する。
    if (mapElem.mapSelectedElemInfo) {
      setMapSelectedElemInfo({ ...mapElem.mapSelectedElemInfo });
    }
  };

  return {
    onTabElementClick,
    showPreviousGeoItem,
    showNextGeoItem,
    getGeoItemById,
  };
}
